import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

const HIGHLIGHT_DURATION = 2000;
const HIGHLIGHT_CLASS = "lime-highlight-element";
const MAX_ATTEMPTS = 30;
const RETRY_DELAY = 150;
const INITIAL_DELAY = 500;

export const HighlightProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<string | null>(null);

  // Capture the hl param immediately when URL changes
  useEffect(() => {
    const hlParam = searchParams.get("hl");

    // console.log("[HighlightProvider] URL changed, hl param:", hlParam);

    if (hlParam) {
      setPendingHighlight(hlParam);

      // Remove the hl param from URL immediately
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("hl");
          return next;
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, searchParams, setSearchParams]);

  // Handle the highlighting separately - use layoutEffect to run after DOM updates
  useLayoutEffect(() => {
    if (!pendingHighlight) {
      return;
    }

    // console.log("[HighlightProvider] Starting highlight for:", pendingHighlight);

    const attemptsRef = { current: 0 };
    let isActive = true;
    const savedPendingHighlight = pendingHighlight; // Capture the value

    const tryHighlight = () => {
      if (!isActive) {
        // console.log("[HighlightProvider] Search cancelled - cleanup called");
        return;
      }

      const safeHighlight = CSS.escape(savedPendingHighlight);
      const targetElement = document.querySelector(
        `[data-hl="${safeHighlight}"]`,
      ) as HTMLElement;

      if (targetElement) {
        // console.log("[HighlightProvider] ✓ Element found:", targetElement);

        // Scroll element into view
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Add highlight class (CSS animation handles the rest)
        targetElement.classList.add(HIGHLIGHT_CLASS);

        // Remove highlight class after duration
        highlightTimeoutRef.current = setTimeout(() => {
          if (targetElement.classList.contains(HIGHLIGHT_CLASS)) {
            targetElement.classList.remove(HIGHLIGHT_CLASS);
            // console.log("[HighlightProvider] Highlight animation completed");
          }
        }, HIGHLIGHT_DURATION);

        // Clear pending highlight
        setPendingHighlight(null);
        return true;
      }

      attemptsRef.current++;
      if (attemptsRef.current < MAX_ATTEMPTS) {
        // console.log(
        //   `[HighlightProvider] Element not found yet, retrying... (${attemptsRef.current}/${MAX_ATTEMPTS})`,
        // );
        timeoutRef.current = setTimeout(tryHighlight, RETRY_DELAY);
        return false;
      }

      console.warn(
        `[HighlightProvider] ✗ Element with data-hl="${savedPendingHighlight}" not found after ${MAX_ATTEMPTS} attempts`,
      );
      setPendingHighlight(null);
      return false;
    };

    // Wait for initial render before starting search
    const initialTimeout = setTimeout(() => {
      if (isActive) {
        // console.log("[HighlightProvider] Initial delay complete, starting search");
        tryHighlight();
      } else {
        // console.log("[HighlightProvider] Cancelled before initial delay complete");
      }
    }, INITIAL_DELAY);

    return () => {
      // console.log("[HighlightProvider] Cleanup called for:", savedPendingHighlight);
      isActive = false;
      clearTimeout(initialTimeout);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, [pendingHighlight]);

  return <>{children}</>;
};
